import { useEffect, useState } from 'react'
import type { Game, Member } from '../../types/game'
import { ResourceIcon } from '../shared/ResourceIcon'
import { assetUrl } from '../../utils/assets'

interface DonationModalProps {
  game: Game
  members: Member[]
  leftMembers?: Member[]
  memberId: string
  onDonate: (receiverId: string, donations: Array<{ resource: string; amount: number }>) => void
  onClose: () => void
}

const RESOURCES = ['nectar', 'pollen', 'honey', 'wax'] as const

function titleCase(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1)
}

interface ResourceStepperProps {
  resource: typeof RESOURCES[number]
  amount: number
  max: number
  onChange: (next: number) => void
  mobile?: boolean
}

function ResourceStepper({ resource, amount, max, onChange, mobile = false }: ResourceStepperProps) {
  return (
    <div className={mobile ? 'donating-mobile-offer-control' : 'donating-offer-resource-control'}>
      <button
        type="button"
        className="donating-stepper donating-stepper-down"
        onClick={() => onChange(Math.max(0, amount - 1))}
        aria-label={`Decrease ${resource}`}
      >
        -
      </button>
      <input
        className={mobile ? 'donating-mobile-input input' : 'donating-resource-input input'}
        value={amount}
        type="number"
        min={0}
        max={max}
        onChange={(event) => onChange(Math.min(max, Math.max(0, parseInt(event.target.value, 10) || 0)))}
      />
      <button
        type="button"
        className="donating-stepper donating-stepper-up"
        onClick={() => onChange(Math.min(max, amount + 1))}
        aria-label={`Increase ${resource}`}
      >
        +
      </button>
      <ResourceIcon resource={resource} />
    </div>
  )
}

export function DonationModal({ game, members, leftMembers = [], memberId, onDonate, onClose }: DonationModalProps) {
  const myMember = members.find((member) => member.id === memberId)
  const myPlayer = myMember?.color ? game.players.find((player) => player.color === myMember.color) : null
  const [selectedReceiverId, setSelectedReceiverId] = useState<string | null>(null)
  const [amounts, setAmounts] = useState<Record<string, number>>({})

  const knownMembers = [...members, ...leftMembers]
  const receiverCandidates = game.players
    .filter((player) => player.color !== myPlayer?.color)
    .map((player) => knownMembers.find((member) => member.color === player.color) ?? null)
    .filter((member): member is Member => member !== null)

  const maxForResource = (resource: string) => myPlayer?.resources[resource as keyof typeof myPlayer.resources] ?? 0

  useEffect(() => {
    if (receiverCandidates.length === 0) {
      if (selectedReceiverId !== null) {
        setSelectedReceiverId(null)
      }
      return
    }

    if (selectedReceiverId !== null && !receiverCandidates.some((member) => member.id === selectedReceiverId)) {
      setSelectedReceiverId(null)
    }
  }, [receiverCandidates, selectedReceiverId])

  const handleSubmit = () => {
    if (!selectedReceiverId) {
      return
    }

    const donations = RESOURCES
      .filter((resource) => (amounts[resource] ?? 0) > 0)
      .map((resource) => ({ resource, amount: amounts[resource] ?? 0 }))

    if (donations.length === 0) {
      return
    }

    onDonate(selectedReceiverId, donations)
    onClose()
  }

  const receiverOptions = receiverCandidates.map((member) => (
    <label key={member.id}>
      <input
        type="radio"
        name="donating-receiver-desktop"
        value={member.id}
        checked={selectedReceiverId === member.id}
        onChange={() => setSelectedReceiverId(member.id)}
      />
      <span>{member.alias}</span>
    </label>
  ))

  return (
    <div className="donating-modal-root">
      <div className="donating-overlay" onClick={onClose} />
      <div className="donating-container">
        <div className="donating tableau">
          <div className="donating-content desktop-shell-view">
            <div className="donating-column">
              <div className="donating-header">
                <div className="donating-title">Donate</div>
                <div className="donating-description">Donate resources to another player</div>
              </div>
              <img src={assetUrl('images', 'donation.png')} alt="" />
            </div>
            <div className="donating-column">
              <div className="donating-body-container">
                <div className="mobile-modal-close-row">
                  <div id="donating-close" className="mobile-modal-close lobby-start" onClick={onClose}>
                    Close
                  </div>
                </div>
                <div className="donating-offer">
                  <div className="donating-offer-title">Your Donation:</div>
                  {RESOURCES.map((resource) => (
                    <div key={resource} className="colony-collapse-event-player-offer-resource donating-offer-resource">
                      <ResourceStepper
                        resource={resource}
                        amount={amounts[resource] ?? 0}
                        max={maxForResource(resource)}
                        onChange={(next) =>
                          setAmounts((prev) => ({
                            ...prev,
                            [resource]: next,
                          }))
                        }
                      />
                    </div>
                  ))}
                </div>
                <div className="donating-player-list">
                  <div className="donating-offer-title">Choose Receiver:</div>
                  <div className="donating-player-options lobby-donation-mode">{receiverOptions}</div>
                </div>
                <div className="donating-actions">
                  <div id="donating-cancel" className="donating-cancel" onClick={onClose}>
                    Cancel
                  </div>
                  <div id="donating-submit" className="donating-submit" onClick={handleSubmit}>
                    Donate
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="donating-mobile-shell mobile-shell-view">
            <div className="mobile-modal-shell donating-mobile-card">
              <div className="mobile-modal-header">
                <div className="mobile-modal-title">Donate</div>
                <div id="donating-mobile-close" className="mobile-modal-close-button lobby-start" onClick={onClose}>Close</div>
              </div>
              <div className="mobile-modal-section">
                <div className="mobile-modal-section-title">Choose Resources</div>
                <div className="mobile-modal-subsection-note" />
                <div className="mobile-resource-input-grid donating-mobile-offer">
                  {RESOURCES.map((resource) => (
                    <div key={`mobile-${resource}`} className="donating-mobile-offer-card">
                      <div className="donating-mobile-offer-label">{titleCase(resource)}</div>
                      <ResourceStepper
                        resource={resource}
                        amount={amounts[resource] ?? 0}
                        max={maxForResource(resource)}
                        onChange={(next) =>
                          setAmounts((prev) => ({
                            ...prev,
                            [resource]: next,
                          }))
                        }
                        mobile
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className="mobile-modal-section">
                <div className="mobile-modal-section-title">Choose Receiver</div>
                <div className="mobile-modal-subsection-note" />
                <div className="mobile-choice-grid donating-mobile-player-options">
                  {receiverCandidates.map((member) => (
                    <label key={`mobile-${member.id}`}>
                      <input
                        type="radio"
                        name="donating-receiver-mobile"
                        value={member.id}
                        checked={selectedReceiverId === member.id}
                        onChange={() => setSelectedReceiverId(member.id)}
                      />
                      <span>{member.alias}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="mobile-modal-footer">
                <div id="donating-mobile-cancel" className="donating-cancel" onClick={onClose}>
                  Cancel
                </div>
                <div id="donating-mobile-submit" className="donating-submit" onClick={handleSubmit}>
                  Donate
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
